import React, { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { Circle } from 'lucide-react';

import { trackingBudget } from 'loot-core/src/client/queries';

import { AlignedText } from '../../../common/AlignedText';
import { Text } from '../../../common/Text';
import { Tooltip } from '../../../ui/tooltip2';
import { View } from '../../../common/View';
import { PrivacyFilter } from '../../../PrivacyFilter';
import { useFormat } from '../../../spreadsheet/useFormat';
import { makeAmountFullStyle } from '../../util';
import { useTrackingSheetValue } from '../TrackingBudgetComponents';

function getCurrencySymbol(code: string): string {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    }).formatToParts(0);
    return parts.find(p => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

type SavedProps = {
  projected: boolean;
  currencyCode?: string;
  style?: CSSProperties;
};
export function Saved({ projected, currencyCode = 'PEN', style }: SavedProps) {
  const { t } = useTranslation();
  const budgetedSaved =
    useTrackingSheetValue(trackingBudget.totalBudgetedSaved) || 0;
  const totalSaved = useTrackingSheetValue(trackingBudget.totalSaved) || 0;
  const format = useFormat();
  const saved = projected ? budgetedSaved : totalSaved;
  const isNegative = saved < 0;
  const diff = totalSaved - budgetedSaved;

  const currencySymbol = getCurrencySymbol(currencyCode);

  const badgeLabel = projected
    ? t('Projected savings')
    : isNegative
      ? t('Overspent')
      : t('Saved');

  const dotColor = projected
    ? 'text-muted-foreground'
    : isNegative
      ? 'text-red-500'
      : 'text-green-500';

  const amountColor = projected
    ? 'var(--color-warningText)'
    : isNegative
      ? 'var(--color-errorTextDark)'
      : 'var(--color-successText)';

  return (
    <Tooltip
      style={{ borderWidth: 1, borderRadius: 6, borderStyle: 'solid', borderColor: 'var(--color-tooltipBorder)', backgroundColor: 'var(--color-tooltipBackground)', color: 'var(--color-tooltipText)', overflow: 'auto', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2), 0 6px 10px -3px rgba(0,0,0,0.1)', fontSize: 14, padding: 10 }}
      content={
        <>
          <AlignedText
            left={t('Projected savings:')}
            right={
              <Text
                style={{
                  ...makeAmountFullStyle(budgetedSaved),
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {format(budgetedSaved, 'financial-with-sign')}
              </Text>
            }
          />
          <AlignedText
            left={t('Difference:')}
            right={
              <Text style={{ ...makeAmountFullStyle(diff), fontFeatureSettings: '"tnum"' }}>
                {format(diff, 'financial-with-sign')}
              </Text>
            }
          />
        </>
      }
      placement="bottom"
      triggerProps={{
        isDisabled: Boolean(projected),
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 13,
          ...style,
        }}
      >
        {/* Status badge pill */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 999,
            backgroundColor: projected
              ? 'var(--color-tableBackground)'
              : isNegative
                ? 'rgba(239,68,68,0.18)'
                : 'rgba(34, 197, 94, 0.18)',
            border: projected
              ? '1px solid var(--color-tableBorder)'
              : isNegative
                ? '1px solid rgba(239,68,68,0.28)'
                : '1px solid rgba(34, 197, 94, 0.28)',
            fontSize: 12,
            fontWeight: 500,
            color: projected
              ? 'var(--color-pageTextLight)'
              : isNegative
                ? 'var(--color-errorTextDark)'
                : 'var(--color-successText)',
          }}
        >
          <Circle
            size={7}
            className={dotColor}
            style={{ fill: 'currentColor' }}
          />
          {badgeLabel}
        </span>

        {/* Amount with currency symbol */}
        <Text
          style={{
            fontFeatureSettings: '"tnum"',
            fontWeight: 600,
            color: amountColor,
          }}
        >
          <PrivacyFilter>
            {format(saved, 'financial')} {currencySymbol}
          </PrivacyFilter>
        </Text>
      </View>
    </Tooltip>
  );
}
